package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LRefreshIcon
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.nav.AppRoute
import dev.lingvo.site.state.WorkspaceSelection
import kotlinx.browser.window

/**
 * Says that another tab moved the workspace out from under this page.
 *
 * The selection lives in local storage so that a contractor who works in one workspace does not
 * re-choose it on every visit, and so that a pasted link cannot silently switch the recipient's
 * workspace. The price is that all tabs share one selection: switch in one and the others carry on
 * rendering a workspace name that is no longer the one their next action will apply to. That price
 * was accepted on the understanding that the other tabs would say so. This is the saying-so.
 *
 * **Never reloads by itself.** An automatic reload discards whatever is typed into the page, which
 * is precisely the loss the whole arrangement was arranged to avoid; a page that helpfully throws
 * away a half-written translation is worse than one showing a stale workspace name. So it states
 * what happened, and the person decides when.
 *
 * **A single-workspace user never sees it, without a count check.** The bar is raised by a `storage`
 * event carrying a slug different from this tab's, and someone with one workspace has nothing else
 * to switch to, so the event never fires with a different value. Testing `Session.organizations.size`
 * would be worse than redundant: that list is loaded once per session, so it is exactly stale in the
 * case where another tab has just created the second workspace.
 *
 * Ranked by the shell beside the other notices rather than drawn by each page, because a page that
 * forgot it would be a page where the cost is unmitigated again.
 */
@Composable
internal fun workspaceDriftNotice(currentPath: String): ShellNotice? {
    val incoming = WorkspaceSelection.incomingSlug ?: return null
    if (!AppRoute.readsSelectedWorkspace(currentPath)) return null

    val rendered = WorkspaceSelection.current()?.name
        ?: t("workspace_drift_banner.this_workspace", "this workspace")
    // Resolved from the memberships this tab already has. When the other tab has just created the
    // workspace — or signed in as someone else — the slug will not be there, and naming the raw slug
    // would be worse than not naming it: the reader has never seen that string.
    val incomingName = Session.organizations.firstOrNull { it.slug == incoming }?.name

    return ShellNotice(
        id = "workspace-drift",
        title = t(
            "workspace_drift_banner.another_tab_switched_workspace",
            "Another tab switched workspace.",
        ),
        // `{rendered}` / `{incoming}` rather than Kotlin templates, which no catalogue can hold: the
        // sentence a reader meets when the ground has moved under the page is the last one that
        // should arrive in a language they do not read.
        description = if (incomingName != null) {
            tf(
                "workspace_drift_banner.this_page_is_still_showing_rendered",
                "This page is still showing {rendered}. Reload to see {incoming}.",
                "rendered" to rendered,
                "incoming" to incomingName,
            )
        } else {
            tf(
                "workspace_drift_banner.this_page_is_still_showing_rendered_2",
                "This page is still showing {rendered}.",
                "rendered" to rendered,
            )
        },
        variant = LAlertVariant.INFO,
        action = {
            LButton(
                label = t("workspace_drift_banner.reload", "Reload"),
                onClick = { window.location.reload() },
                variant = LButtonVariant.SECONDARY,
                size = LButtonSize.SM,
                leadingIcon = { LRefreshIcon() },
            )
        },
    )
}
